import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { AxiosError } from "axios";

import { PennText } from "@/components/ui";
import { useAppDispatch } from "../../stores/hooks";
import { setLogin } from "@penn/web-common";
import { OIDC_ENABLED } from "@penn/web-common";
import { APP_CONTEXT } from "@/config/app-context";
import {
  beginLogin,
  completeLogin,
  clearSilentAttemptMarker,
  consumeReturnTo,
  consumeSilentReturnTo,
  endSession,
  isSilentAttemptPending,
  LocalSignInStateError,
} from "@penn/web-common";
import { sessionExchangeRequest } from "@penn/web-common";
import { setCsrfToken } from "@penn/web-common";
import { fetchMe } from "@penn/web-common";
import { isInternalIdentity } from "@penn/web-common";

function errorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { error?: { message?: string } }
      | undefined;
    if (data?.error?.message) return data.error.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return "Sign-in could not be completed. Please try again.";
}

// Human-friendly text for the OIDC/OAuth error codes the issuer forwards on the
// callback (RFC 6749 §4.1.2.1 + the issuer's own). Falls back to the raw code.
function oauthErrorMessage(code: string, description: string | null): string {
  if (description && description.trim()) return description.trim();
  switch (code) {
    case "access_denied":
      return "Access was denied by the identity provider. Your account may not be assigned to this application, or a sign-in policy blocked it.";
    case "login_required":
    case "interaction_required":
      return "The identity provider needs you to sign in again. Please retry.";
    case "invalid_request":
      return "The sign-in request was rejected as invalid. Please retry from Sign in.";
    case "server_error":
    case "temporarily_unavailable":
      return "The identity provider had a temporary error. Please retry.";
    default:
      return `Sign-in was rejected (${code}).`;
  }
}

// OIDC redirect target (AUTH_MODE=oidc). The issuer sends the browser here with
// ?code&state; we redeem the code for the issuer id_token and run the SAME
// session exchange the OTP flow uses (svc-02 routes on `iss`). A cross-app
// (wrong-app-for-account) rejection surfaces as the exchange's 403 — no
// client-side group check needed. An identity with no Configurator row is
// JIT-provisioned by svc-02 from the issuer's `pem:region` claim, so the
// exchange's 401 user-not-registered here means svc-02 REFUSED to provision
// (staff identity, or a token without a usable region) — never "go fill in the
// app-local sign-up form", which in this mode belongs to the Cognito flow.
export function CallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<string | null>(null);
  const [localRetry, setLocalRetry] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    // Dark unless the deployment opted in: the route exists in every build so
    // the redirect URI is stable, but nothing is redeemed without the flag.
    if (!OIDC_ENABLED) return;
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const oidcError = params.get("error");
    const errorDescription = params.get("error_description");
    const errorUri = params.get("error_uri");

    (async () => {
      // The identity provider bounced back an OAuth error (RFC 6749) instead of
      // a code — surface the actual code + description so the failure is
      // diagnosable (e.g. an Entra AADSTS reason) rather than a generic retry.
      if (oidcError) {
        // A silent attempt (ISC-403) the issuer declined. login_required: there
        // was no live SSO session to resume. interaction_required: there was,
        // but the issuer must show the user a page first (today, accepting
        // updated Terms of Use / Privacy Policy, identity SONPF-11), which
        // prompt=none forbids. The user never asked for this, so neither may
        // surface as a sign-in error — land them on the passive /login exactly
        // as an expired token did before; its Sign in button reaches that page.
        // The suppression marker is left in place deliberately: clearing it here
        // would let the next reload re-attempt, which is the loop AC3 forbids.
        if (
          (oidcError === "login_required" || oidcError === "interaction_required") &&
          isSilentAttemptPending()
        ) {
          navigate("/login", { replace: true });
          return;
        }
        setError(oauthErrorMessage(oidcError, errorDescription));
        setDetails(
          [
            `error: ${oidcError}`,
            errorDescription ? `description: ${errorDescription}` : null,
            errorUri ? `more: ${errorUri}` : null,
          ]
            .filter(Boolean)
            .join("\n"),
        );
        return;
      }
      if (!code || !state) {
        setError("Sign-in could not be completed. Please try again.");
        setDetails("No authorization code was returned to the app.");
        return;
      }
      // Hoisted so the catch can tell a staff identity from a customer one when
      // the exchange refuses an unregistered user (see below).
      let idToken = "";
      // Read before the exchange: the silent returnTo is consumed below, and
      // the marker is released only once svc-02 has accepted the identity.
      const wasSilent = isSilentAttemptPending();
      try {
        idToken = await completeLogin(code, state);
        const exchanged = await sessionExchangeRequest(idToken, {
          appContext: APP_CONTEXT,
        });
        // Only now: a redeemed code proves the SSO cookie was live, not that
        // the identity is still authorized. Clearing on redemption alone would
        // let a revoked user — live cookie, refused by svc-02 — silently
        // re-mint on every reload forever.
        if (wasSilent) clearSilentAttemptMarker();
        // Cookie-session half of the exchange (ADR-0036): the CSRF value has to
        // be echoed on /refresh and /logout, and the body copy is the reliable
        // source — the same line the OTP path has in penn-session-exchange.ts.
        if (exchanged.csrfToken) setCsrfToken(exchanged.csrfToken);
        dispatch(
          setLogin({ accessToken: exchanged.accessToken, user: exchanged.user }),
        );
        try {
          const me = await fetchMe();
          dispatch(
            setLogin({
              accessToken: exchanged.accessToken,
              user: me.user,
              assumptions: me.assumptions,
            }),
          );
        } catch (meErr) {
          // Non-blocking: sign-in still succeeds on the exchange's user shape.
          // Log a redacted summary only — never the AxiosError object, whose
          // config carries request headers.
          const meStatus =
            meErr instanceof AxiosError ? meErr.response?.status : undefined;
          const meCode =
            meErr instanceof AxiosError
              ? (meErr.response?.data as { error?: { code?: string } })?.error
                  ?.code
              : undefined;
          console.error("[CallbackPage] fetchMe overlay failed", {
            status: meStatus ?? null,
            code: meCode ?? null,
          });
        }
        // AC2: the page the silent attempt was fired from, not the app root.
        navigate(wasSilent ? consumeSilentReturnTo() : consumeReturnTo(), {
          replace: true,
        });
      } catch (err) {
        if (err instanceof AxiosError) {
          const code = (err.response?.data as { error?: { code?: string } })
            ?.error?.code;
          if (code === "user-not-registered") {
            // svc-02 refused to mint a session for a verified identity with no
            // Configurator row. Staff cannot self-register (their rows are
            // provisioned by an administrator); an external identity should
            // have been JIT-provisioned, so a refusal means the token carried
            // no usable region — also not something a sign-up form here fixes.
            // Either way, say so and offer the account switch.
            setError(
              isInternalIdentity(idToken)
                ? "Your PennEngineering account is not set up in the Configurator yet. Ask an administrator to grant you access."
                : "Your account is not set up in the Configurator yet. Please complete your PennEngineering profile, or contact support if this persists.",
            );
            setDetails(null);
            return;
          }
          setError(errorMessage(err));
          setDetails(
            [
              err.response?.status ? `status: ${err.response.status}` : null,
              code ? `code: ${code}` : null,
            ]
              .filter(Boolean)
              .join("\n") || null,
          );
        } else {
          setError(errorMessage(err));
          if (err instanceof LocalSignInStateError) setLocalRetry(true);
          else if (err instanceof Error) setDetails(err.message);
        }
      }
    })();
  }, [params, navigate, dispatch]);

  if (!OIDC_ENABLED) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-lg text-center">
        {error ? (
          <>
            <PennText as="h1" size="2xl" align="center" color="on-navy" font="semibold">
              Sign-in problem
            </PennText>
            <PennText
              as="p"
              size="base"
              align="center"
              color="on-navy-secondary"
              className="mt-2"
            >
              {error}
            </PennText>
            {details && (
              <pre className="mt-6 text-left font-mono text-[12px] leading-[18px] text-on-navy-tertiary bg-white/5 border border-on-navy-border rounded-xl px-3 py-2 whitespace-pre-wrap break-words">
                {details}
              </pre>
            )}
            {localRetry ? (
              // A purely local PKCE/state failure — the issuer session is not
              // implicated, so restart the flow in-app rather than ending it.
              <button
                className="mt-8 px-5 py-2 rounded-xl text-sm font-semibold bg-penn-500 hover:bg-penn-700 text-white shadow-soft cursor-pointer transition-colors"
                onClick={() => {
                  setError(null);
                  setDetails(null);
                  setLocalRetry(false);
                  ranRef.current = false;
                  void beginLogin();
                }}
              >
                Try signing in again
              </button>
            ) : (
              // Full issuer logout, not navigate("/login"): /login re-runs
              // beginLogin() and the issuer's live SSO cookie would silently
              // re-mint a code for the SAME rejected account, returning here.
              // Ending the issuer session is the only real escape / account switch.
              <button
                className="mt-8 px-5 py-2 rounded-xl text-sm font-semibold bg-penn-500 hover:bg-penn-700 text-white shadow-soft cursor-pointer transition-colors"
                onClick={() => endSession()}
              >
                Sign out and try another account
              </button>
            )}
          </>
        ) : (
          <PennText as="p" size="base" align="center" color="on-navy-secondary">
            Signing you in…
          </PennText>
        )}
      </div>
    </div>
  );
}
